import * as React from "react";
import Dropzone from "react-dropzone";
import FontAwesome from 'react-fontawesome';
import PerfectScrollbar from "perfect-scrollbar-react";
import ImagePicker from "react-image-picker";
import { PulseLoader } from "react-spinners";
import { Button, Label } from "reactstrap";
import { toastr } from "react-redux-toastr";
import SidebarContainer from "src/modules/Routing/Sidebar/SidebarContainer";
import ConfirmationModal from "src/share/components/ConfirmationModal/ConfirmationModal";
import ProviderSelector from "./components/ProviderSelector";
import MasonryGallery from "src/share/components/Masonry/MasonryGallery";
import MasonrySkeleton from "src/share/components/Masonry/MasonrySkeleton";
import InputContainer from "./components/InputContainer";
import ImageErrorContainer from "./components/ImageErrorContainer";
import useProvider from "./hooks/useProvider";
import withProviderGallery from "./components/withProviderGallery";
import { readFileAsDataURL } from "src/share/utils/fileHandler";
import { IVisualSearchProps, VisualSearchProviders, VisualSearchActions } from "src/share/types/visualSearch";
import { By } from "src/share/types/visualSearch";
import "perfect-scrollbar-react/dist/style.min.css";
import "./VisualSearch.css";
import { RoutesModules } from "src/modules/Routing/types";

const { useEffect, createRef } = React;

const fileInputRef: React.RefObject<HTMLInputElement> = createRef();

const VisualSearch: React.SFC<IVisualSearchProps> = (props) => {
  const [provider, setProvider] = React.useState(VisualSearchProviders.PIMEYES)
  const { state, actions } = useProvider(provider);
  const [imageName, setImageName] = React.useState('');
  useEffect(() => {
    if (props.location.state) {
      actions.dispatch({ type: VisualSearchActions.setThumbnail, payload: { image: props.location.state.image, by: props.location.state.by } });
    }
  }, []);
  function getImageDescription() {
    if (state.error) {
      return <ImageErrorContainer error={state.error} />
    }
    if (state.isFaceLoading) {
      return <PulseLoader loading={state.isFaceLoading} color={"#00353b"} sizeUnit={"px"} size={10} />;
    }
    return <InputContainer handleDropAccepted={handleDropAccepted} fileInputRef={fileInputRef} />
  }
  function handleImageUpload() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }
  function handleRemoveImage(evt) {
    const imagesWithoutErrors = state.images.filter((image) => image.imageUrl !== evt.target.src);
    actions.dispatch({ type: VisualSearchActions.setImages, payload: imagesWithoutErrors })
  }
  async function handleDropAccepted(files) {
    const file = files[0];
    const base64 = await readFileAsDataURL(file, (result) => {
      return (result.target && result.target.result) || "";
    });
    setImageName(file.name)
    actions.dispatch({ type: VisualSearchActions.setThumbnail, payload: { image: base64, by: By.Base64 } })
  }
  function handleDropRejected() {
    actions.dispatch({ type: VisualSearchActions.error, payload: "Invalid file format." });
    toastr.error("Invalid file format", "You can only upload images.");
  }
  const onRemoveImageSelected = () => {
    setImageName('');
    actions.dispatch({ type: VisualSearchActions.setThumbnail, payload: { image: "", by: By.Base64 } });
    actions.dispatch({ type: VisualSearchActions.reset });
  }
  return (
    <div className="visual-search">
      <div className="visual-search__header">
        <h3>Visual Search</h3>
        <div className="warning-card">
          <p>
            ** All these images are provided by an external service. <br />
            We have no control over the content that is shown in their source page.
          </p>
        </div>
      </div>
      <div className={`modal-container ${state.providerModalToggle ? "active" : ""}`}>
        <div className="backdrop" onClick={() => actions.dispatch({ type: VisualSearchActions.toggleProviderModal })} />
        <ProviderSelector
          onModalToggle={() => actions.dispatch({ type: VisualSearchActions.toggleProviderModal })}
          onProviderChange={(selectedProvider) => {
            setProvider(selectedProvider)
            actions.dispatch({ type: VisualSearchActions.toggleProviderModal });
          }}
        />
      </div>
      <ConfirmationModal
        toggle={() => actions.dispatch({ type: VisualSearchActions.toggleFaceModal })}
        isOpen={state.showFaceModal}
        title="Image Search"
        description="Choose a face with which to perfom a search:"
        textInput={
          <ImagePicker
            images={state.faces.map((face) => ({ src: "data:image/jpeg;base64," + face.image, value: face.id }))}
            onPick={(image) => actions.dispatch({ type: VisualSearchActions.setFace, payload: image.value })}
          />
        }
        disabledButton={!state.selectedFace}
        onContinue={() => actions.sendImage(state.selectedFace)}
        customClassName={"visual-search__face-modal"}
      />
      <div className="visual-search__container">
        <div className="visual-search-form-container">
          <div className={`visual-search__image-card ${state.error ? "image-error" : ""}`} >
            {(imageName || (state.by === By.URL && state.thumbnail)) &&
              <div className="image-name-remove-container">
                <div>
                  <Label className="input-label subFont">{state.by === By.URL ? state.thumbnail : imageName}</Label>
                </div>
                <div className="remove-image-container">
                  <Button disabled={state.isLoading || state.isFaceLoading} className="btn btn-light remove-button" onClick={() => onRemoveImageSelected()} >
                    <FontAwesome name='times-circle' />
                  </Button>  </div>
              </div>}
            <div className="drop-zone-container" onClick={handleImageUpload} style={{ height: (imageName || (state.by === By.URL && state.thumbnail)) ? '80%' : '100%' }}>
              <Dropzone
                className="drop-zone-fluid"
                activeClassName="active"
                multiple={false}
                accept="image/jpeg, image/png"
                onDropAccepted={handleDropAccepted}
                onDropRejected={handleDropRejected}
              >
                {state.thumbnail ? <img src={state.thumbnail} alt="Searched Image" /> : <span className="fa fa-paperclip fa-2x" aria-hidden="true" />}
              </Dropzone>
              {getImageDescription()}
            </div>
          </div>
          <button className="visual-search__provider-toggle" onClick={() => actions.dispatch({ type: VisualSearchActions.toggleProviderModal })}>
            {provider}<span className="fa fa-caret-down" area-hidden="true" />
          </button>
        </div>
        <div className="visual-search__similar">
          <PerfectScrollbar>
            {state.isLoading ? <MasonrySkeleton /> :
              state.images.length > 0 &&
              <MasonryGallery>
                {props.children({ provider, images: state.images, removeInvalidImage: handleRemoveImage, isFetchingData: state.isFetchingData })}
              </MasonryGallery>
            }
            {state.loadMore && <Button className="btn btn-gray btn-load-more" onClick={() => actions.fetchImages(state.thumbnail, By.URL)}>Load more</Button>}
          </PerfectScrollbar>
        </div>
      </div>
    </div>
  );
};

export default SidebarContainer(RoutesModules.SEARCH)(withProviderGallery(VisualSearch));
